import React, { useState, useContext, useEffect, useCallback, useLayoutEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Collapse } from "bootstrap";
import { useTranslation } from "react-i18next";
import { PersonSearch, SportsMma, Leaderboard, EmojiEvents, AttachMoney, SsidChart, Games, Dashboard, WorkspacePremium, ExpandMore } from "@mui/icons-material";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { SvgIconTypeMap } from "@mui/material";
import 'bootstrap/dist/css/bootstrap.css';
// Bootstrap's JS comes in via the `Collapse` import above and nothing else. Importing it a second
// time here as 'bootstrap/dist/js/bootstrap.js' resolved to a *different* module than 'bootstrap',
// so every plugin registered its data-api listeners twice: one click on a dropdown opened it (first
// copy) and immediately closed it again (second copy), which is why the profile and language menus
// stopped responding.
import { admins, baseURL } from "../Constants";
import { AuthContext } from "../context/AuthContext";
import SearchBar from "./SearchBar/SearchBar";
import LanguageSelector from "./LanguageSelector";
import logo from '../content/logo.png';
import '../styles/theme.css';
import PlayerImage from "./PlayerImage";
import { useLiveMatches } from "../services/LiveService";

interface Icon {
    icon: OverridableComponent<SvgIconTypeMap<{}, "svg">>;
    displayName: string;
    // Shown instead of displayName whenever the row is measured to not fit. Which width that
    // happens at depends on who is looking: admins get an extra top-level item, and the label
    // lengths change with the language, so this cannot be a fixed breakpoint.
    shortDisplayName?: string;
    link: string;
    dropdownHeader?: string;
    dropdown?: { displayName: string; link: string; sublabel?: string }[];
}

// How the desktop row gives up width, cheapest first: tier n wears the first n of these. They are
// classes on the row and their CSS lives in theme.css, which is what lets the measurement try a
// tier on and take it off again without a re-render in the middle.
const NAV_TIER_CLASSES = ['nav-short-labels', 'nav-admin-icon-only', 'nav-no-icons', 'nav-tight-account'];

const Header = () => {
    const [navLinks, setNavLinks] = useState<Icon[]>([]);
    // Desktop nav row. Its tier is worn as classes rather than held in state: Bootstrap's collapse
    // plugin writes `show`/`collapsing` onto this same element, and a React re-render of className
    // would wipe them out from under it.
    const navRowRef = useRef<HTMLDivElement>(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
    // Which mobile nav group is expanded. Accordion — one at a time, so the menu stays short.
    const [openGroup, setOpenGroup] = useState<string | null>(null);
    const { user, setToken } = useContext(AuthContext);
    const { t } = useTranslation('navigation');
    const location = useLocation();
    // While maps are being played, "Kampe" carries a red dot + count (its Live tab opens first).
    const liveCount = useLiveMatches().live.length;
    const hasLive = liveCount > 0;

    // Track mobile state
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 992);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Add CSS for LFT hover dropdown (desktop only)
    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            /* Pure CSS solution for hover dropdown - desktop only */
            @media (min-width: 992px) {
                .nav-hover-dropdown .dropdown-menu {
                    display: none;
                    position: absolute;
                    top: 100%;
                    left: 0;
                    margin-top: 0;
                    padding-top: 0;
                }

                .nav-hover-dropdown:hover .dropdown-menu,
                .nav-hover-dropdown .dropdown-menu:hover {
                    display: block;
                }

                /* Ensure no gap between trigger and dropdown */
                .nav-hover-dropdown .dropdown-toggle {
                    padding-bottom: 0.5rem;
                }
            }

            /* Live marker on "Kampe": only rendered while maps are being played */
            .nav-live-dot {
                display: inline-block;
                width: 7px;
                height: 7px;
                border-radius: 50%;
                background: #ef4444;
                animation: navLivePulse 1.6s ease-in-out infinite;
            }
            @keyframes navLivePulse {
                0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.6); }
                60% { box-shadow: 0 0 0 5px rgba(239, 68, 68, 0); }
            }
            @media (prefers-reduced-motion: reduce) {
                .nav-live-dot { animation: none; }
            }
            .nav-live-count {
                display: inline-flex;
                align-items: center;
                gap: 5px;
                margin-left: 6px;
                padding: 1px 7px;
                border-radius: 10px;
                background: rgba(239, 68, 68, 0.18);
                color: #f87171;
                font-size: 0.7rem;
                font-weight: 700;
                vertical-align: middle;
            }

            /* Premium chip (account zone) */
            .premium-chip {
                display: inline-flex;
                align-items: center;
                gap: 6px;
                padding: 5px 13px;
                border-radius: 20px;
                background: rgba(241, 92, 37, 0.12);
                border: 1px solid rgba(241, 92, 37, 0.5);
                color: #ff9a5c;
                font-weight: 600;
                font-size: 0.9rem;
                line-height: 1;
                text-decoration: none;
                white-space: nowrap;
                transition: background 0.2s ease, color 0.2s ease;
            }
            .premium-chip:hover {
                background: rgba(241, 92, 37, 0.2);
                color: #ffb27a;
            }

            /* "Spillermarked"-style dropdown: section header + right-aligned sub-labels */
            .nav-dropdown-header {
                font-size: 0.65rem;
                font-weight: 700;
                letter-spacing: 0.6px;
                text-transform: uppercase;
                color: rgba(255, 255, 255, 0.4);
                padding: 6px 16px 4px;
            }
            .dropdown-item.nav-dropdown-item {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 20px;
            }
            .nav-dropdown-sub {
                font-size: 0.78rem;
                color: rgba(255, 255, 255, 0.4);
                white-space: nowrap;
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    useEffect(() => {
        const defaultLinks: Icon[] = [
            {
                icon: SportsMma,
                displayName: t('header.matches'),
                link: "/matches"
            },
            {
                icon: Leaderboard,
                displayName: t('header.standings'),
                link: "/standings"
            },
            {
                // "Spillermarked": LFT / LFP / Transfers grouped under one dropdown.
                icon: PersonSearch,
                displayName: t('header.lft'),
                link: "/lft",
                dropdownHeader: t('header.playerMarket'),
                dropdown: [
                    {
                        displayName: t('header.lft'),
                        link: "/lft",
                        sublabel: t('header.lftSub')
                    },
                    {
                        displayName: t('header.lfp'),
                        link: "/lfp",
                        sublabel: t('header.lfpSub')
                    },
                    {
                        displayName: t('header.transfers'),
                        link: "/transfers",
                        sublabel: t('header.transfersSub')
                    }
                ]
            },
            {
                icon: Leaderboard,
                displayName: t('header.predicts'),
                link: "/standings/predict"
            },
            {
                icon: SsidChart,
                displayName: t('header.fantasy'),
                link: "/fantasy"
            },
            {
                icon: EmojiEvents,
                displayName: t('header.leaderboard'),
                link: "/leaderboard"
            },
            {
                icon: AttachMoney,
                displayName: t('header.hallOfFame'),
                shortDisplayName: t('header.hallOfFameShort'),
                link: "/donations"
            },
            {
                icon: Games,
                displayName: t('header.games'),
                link: "/games",
                dropdown: [
                    {
                        displayName: t('header.allGames'),
                        link: "/games"
                    },
                    {
                        displayName: t('header.gamesLeaderboard'),
                        link: "/games/leaderboard"
                    },
                    {
                        displayName: t('header.powerstrikle'),
                        link: "/powerstrikle"
                    },
                    {
                        displayName: t('header.ratingDuel'),
                        link: "/rating-duel"
                    },
                    {
                        displayName: t('header.logoPuzzle'),
                        link: "/logo-puzzle"
                    },
                    {
                        displayName: t('header.teamRoster'),
                        link: "/team-roster"
                    },
                    {
                        displayName: t('header.whosThatPro'),
                        link: "/whos-that-pro"
                    },
                    {
                        displayName: t('header.tierlist'),
                        link: "/tierlist"
                    }
                ]
            }
        ];

        setNavLinks(defaultLinks);
        // Nothing user-specific is left in the nav, so translations are the only trigger.
    }, [t]);

    const steamLogin = async () => {
        const url = baseURL + "/Auth/steam";
        window.location.href = url;
    };

    const logout = () => {
        setToken(null);
    };

    // Determine if current user is an admin
    const isAdmin = user && admins.includes(user.steamID);

    // Close mobile navbar when a link is clicked.
    // Drives Bootstrap directly instead of synthesising a click on the toggler: mid-transition the
    // collapse carries `collapsing` rather than `show`, so the old `contains('show')` guard made a
    // tap during those ~350ms a no-op and left the menu hanging open over the page.
    // `toggle: false` so getOrCreateInstance can't open a closed menu as a side effect.
    const closeNavbar = useCallback(() => {
        const navbar = document.getElementById('navbarNavDropdown');
        if (navbar) {
            Collapse.getOrCreateInstance(navbar, { toggle: false }).hide();
        }
        // Collapse any expanded group too, so reopening the menu starts from the short list.
        setOpenGroup(null);
    }, []);

    // Close on every navigation, not just the ones that go through an onClick — browser back/forward
    // and any programmatic navigate() would otherwise leave the menu covering the new page.
    useEffect(() => {
        closeNavbar();
    }, [location.pathname, closeNavbar]);

    // Fallback for the mobile account menus. They open upward (theme.css) so they normally land in
    // view with no scrolling; this only bites on a viewport short enough that the menu is taller
    // than the space above its toggle, where it would clip against the top of the scroll box.
    useEffect(() => {
        const nav = document.getElementById('navbarNavDropdown');
        if (!nav) return;
        const onShown = (e: Event) => {
            if (window.innerWidth >= 992) return;
            const menu = (e.target as HTMLElement).parentElement?.querySelector('.dropdown-menu');
            menu?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        };
        nav.addEventListener('shown.bs.dropdown', onShown);
        return () => nav.removeEventListener('shown.bs.dropdown', onShown);
    }, []);


    // Pick the densest tier the row still fits in. Measured rather than pinned to a breakpoint:
    // an admin carries an extra top-level item and the label widths change with the language, so
    // the width where the row stops fitting is not a constant. Every tier is tried on the real
    // element, so the answer never depends on which tier is currently showing - there is no state
    // to get stuck in and no width to remember.
    useLayoutEffect(() => {
        const row = navRowRef.current;
        if (!row) return undefined;

        // What a flex child would take if nothing squeezed it. Both lists shrink, so their laid
        // out width is the squeeze, not the need - the need is what decides the tier.
        const widthAt = (el: HTMLElement, sizing: string) => {
            const before = el.getAttribute('style') ?? '';
            el.style.flex = '0 0 auto';
            el.style.width = sizing;
            const width = el.getBoundingClientRect().width;
            el.setAttribute('style', before);
            return width;
        };

        const wear = (tier: number) =>
            NAV_TIER_CLASSES.forEach((name, index) => row.classList.toggle(name, index < tier));

        const measure = () => {
            if (window.innerWidth < 992) {
                // Below the Bootstrap breakpoint the nav is a stacked menu with room to spare.
                wear(0);
                return;
            }

            const items = row.querySelector<HTMLElement>('ul.navbar-nav.mr-auto');
            const account = row.querySelector<HTMLElement>('ul.navbar-nav.ms-auto');
            if (!items || !account) return;

            // Try each tier on in turn and stop at the first that fits, which leaves it worn. If
            // none do, the loop ends wearing the densest one and the labels ellipsis instead.
            for (let tier = 0; tier <= NAV_TIER_CLASSES.length; tier++) {
                wear(tier);
                // The account zone hands over its search box before it gives up anything else, so
                // min-content is the floor the nav has to clear - not the width it would like.
                const available = row.clientWidth - widthAt(account, 'min-content');
                if (widthAt(items, 'max-content') <= available) break;
            }
        };

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(row);
        window.addEventListener('resize', measure);
        // Nav labels are measured text, so the fallback font's widths are not the answer.
        document.fonts?.ready.then(measure).catch(() => undefined);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [navLinks, isAdmin, user, hasLive]);

    return (
        <nav className="navbar navbar-expand-lg py-0 mb-3">
            <Link className="navbar-brand ms-3" to="/">
                <img src={logo} alt="logo" width="35" height="35" className="d-inline-block align-text-top me-2" />
                <span className="d-none d-md-inline">PowerStats</span>
            </Link>
            <button className="navbar-toggler"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#navbarNavDropdown"
                aria-controls="navbarNavDropdown"
                aria-expanded="false"
                aria-label="Toggle navigation"
            >
                <span className="navbar-toggler-icon"></span>
            </button>
            <div className="collapse navbar-collapse" id="navbarNavDropdown" ref={navRowRef}>
                <ul className="navbar-nav mr-auto">
                    {navLinks.map((link, index) => {
                        // Items with dropdown — hover on desktop, collapsible group on mobile
                        if (link.dropdown) {
                            if (isMobile) {
                                // Collapsed by default. Flattening these inline turned 8 nav entries
                                // into 18 rows, which is what made the phone menu unusable.
                                const groupId = `nav-group-${index}`;
                                const isOpen = openGroup === groupId;
                                return (
                                    <li className="nav-item m-1" key={index}>
                                        <button
                                            type="button"
                                            className="nav-link nav-group-toggle"
                                            aria-expanded={isOpen}
                                            aria-controls={groupId}
                                            onClick={() => setOpenGroup(isOpen ? null : groupId)}
                                        >
                                            {/* dropdownHeader names the group ("Spillermarked"); displayName is
                                                the parent's own destination ("LFT"), which would repeat the
                                                first child. Games has no header, so it falls back. */}
                                            <span><link.icon /> {link.dropdownHeader ?? link.displayName}</span>
                                            <ExpandMore className="nav-group-caret" />
                                        </button>
                                        {isOpen && (
                                            <ul className="nav-group-items" id={groupId}>
                                                {link.dropdown.map((dropdownItem, dropdownIndex) => (
                                                    <li key={`${index}-${dropdownIndex}`}>
                                                        <Link className="nav-link" to={dropdownItem.link} onClick={closeNavbar}>
                                                            {dropdownItem.displayName}
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </li>
                                );
                            }
                            return (
                                <li className="nav-item dropdown nav-hover-dropdown m-1" key={index}>
                                    <Link
                                        className="nav-link dropdown-toggle"
                                        to={link.link}
                                        onClick={closeNavbar}
                                    >
                                        <link.icon /> {link.displayName}
                                    </Link>
                                    <ul className="dropdown-menu">
                                        {link.dropdownHeader && (
                                            <li className="nav-dropdown-header">{link.dropdownHeader}</li>
                                        )}
                                        {link.dropdown.map((dropdownItem, dropdownIndex) => (
                                            <li key={dropdownIndex}>
                                                <Link className="dropdown-item nav-dropdown-item" to={dropdownItem.link} onClick={closeNavbar}>
                                                    <span>{dropdownItem.displayName}</span>
                                                    {dropdownItem.sublabel && (
                                                        <span className="nav-dropdown-sub">{dropdownItem.sublabel}</span>
                                                    )}
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                </li>
                            );
                        }

                        // Render regular links as usual
                        return (
                            <li className="nav-item m-1" key={index}>
                                <Link className="nav-link" to={link.link} onClick={closeNavbar}>
                                    <link.icon />{' '}
                                    {link.shortDisplayName ? (
                                        <>
                                            <span className="nav-label-full">{link.displayName}</span>
                                            <span className="nav-label-short">{link.shortDisplayName}</span>
                                        </>
                                    ) : (
                                        <span>{link.displayName}</span>
                                    )}
                                    {link.link === "/matches" && hasLive && (
                                        <span className="nav-live-count">
                                            <span className="nav-live-dot" />{liveCount}
                                        </span>
                                    )}
                                </Link>
                            </li>
                        );
                    })}

                    {/* Content Hub - admin only */}
                    {isAdmin && (
                        isMobile ? (
                            <li className="nav-item m-1">
                                <button
                                    type="button"
                                    className="nav-link nav-group-toggle"
                                    aria-expanded={openGroup === 'nav-group-content'}
                                    aria-controls="nav-group-content"
                                    onClick={() => setOpenGroup(openGroup === 'nav-group-content' ? null : 'nav-group-content')}
                                >
                                    <span><Dashboard /> {t('header.contentHub')}</span>
                                    <ExpandMore className="nav-group-caret" />
                                </button>
                                {openGroup === 'nav-group-content' && (
                                    <ul className="nav-group-items" id="nav-group-content">
                                        <li>
                                            <Link className="nav-link" to="/content" onClick={closeNavbar}>
                                                {t('header.contentHub')}
                                            </Link>
                                        </li>
                                        <li>
                                            <Link className="nav-link" to="/admin/tournaments" onClick={closeNavbar}>
                                                Tournaments
                                            </Link>
                                        </li>
                                    </ul>
                                )}
                            </li>
                        ) : (
                            <li className="nav-item dropdown nav-hover-dropdown m-1 nav-admin-item">
                                <Link className="nav-link dropdown-toggle" to="/content" onClick={closeNavbar} title={t('header.contentHub')}>
                                    <Dashboard /> <span className="nav-label-admin">{t('header.contentHub')}</span>
                                </Link>
                                <ul className="dropdown-menu">
                                    <li>
                                        <Link className="dropdown-item" to="/content" onClick={closeNavbar}>
                                            {t('header.contentHub')}
                                        </Link>
                                    </li>
                                    <li>
                                        <Link className="dropdown-item" to="/admin/tournaments" onClick={closeNavbar}>
                                            Tournaments
                                        </Link>
                                    </li>
                                </ul>
                            </li>
                        )
                    )}
                </ul>
                <ul className="navbar-nav ms-auto m-1 d-flex align-items-center">
                    <div className="d-flex align-items-center">
                        {/* Premium: an orange chip in the account zone. */}
                        <Link to="/premium" className="premium-chip me-2" onClick={closeNavbar}>
                            <WorkspacePremium sx={{ fontSize: 17 }} />
                            <span>Premium</span>
                        </Link>
                        <SearchBar onResultClick={closeNavbar} />
                        <div className="ms-2">
                            <LanguageSelector />
                        </div>
                    </div>
                    {user ? (
                        <div className='dropdown ms-2'>
                            <button className='btn btn-primary profile-btn dropdown-bs-toggle' type='button' data-bs-toggle='dropdown' aria-expanded='false'>
                                <PlayerImage id={user.steamID} width={24} />
                                <div className='d-inline-block align-middle ms-2'>
                                    <span className="navbar-playername">{user.playerName}</span>
                                </div>
                            </button>
                            <ul className='dropdown-menu'>
                                <li>
                                    <Link className='dropdown-item' to={`/player/${user.steamID}`} onClick={closeNavbar}>
                                        {t('userMenu.myProfile')}
                                    </Link>
                                </li>
                                <li>
                                    <Link className='dropdown-item' to={`/player/${user.steamID}/edit`} onClick={closeNavbar}>
                                        {t('userMenu.editProfile')}
                                    </Link>
                                </li>
                                <li>
                                    <button className='dropdown-item' type='button' onClick={() => { logout(); closeNavbar(); }}>
                                        {t('userMenu.logout')}
                                    </button>
                                </li>
                            </ul>
                        </div>
                    ) : (
                        <button className="btn btn-default" onClick={steamLogin}>
                            <img src="https://steamcommunity-a.akamaihd.net/public/images/signinthroughsteam/sits_large_noborder.png" alt="steamLogo" />
                        </button>
                    )}
                </ul>
            </div>
        </nav>
    );
};

export default Header;